import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { ArrowUpRight, Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Wordmark } from '@/components/generals/wordmark'
import { navLinks } from '@/constants/site'

const SiteHeader: FC = (): ReactNode => {
    const [open, setOpen] = useState(false)
    const router = useRouter()

    useEffect(() => {
        const close = () => setOpen(false)
        router.events.on('routeChangeStart', close)
        router.events.on('hashChangeStart', close)

        return () => {
            router.events.off('routeChangeStart', close)
            router.events.off('hashChangeStart', close)
        }
    }, [router.events])

    return (
        <header className='sticky top-0 z-50 border-b border-ink/10 bg-bone/85 backdrop-blur-md'>
            <div className='mx-auto flex h-16 max-w-7xl items-center justify-between gap-6 px-5 sm:px-8'>
                <Link href='/' className='rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-signal' aria-label='Unslop Studio home'>
                    <Wordmark />
                </Link>

                <nav aria-label='Main' className='hidden items-center gap-8 md:flex'>
                    {navLinks.map(link => (
                        <Link
                            key={link.href}
                            href={link.href}
                            className='text-sm font-medium text-ink/70 transition-colors hover:text-ink focus-visible:text-ink focus-visible:underline focus-visible:outline-none'
                        >
                            {link.label}
                        </Link>
                    ))}
                </nav>

                <div className='flex items-center gap-2'>
                    <Link
                        href='/#contact'
                        className='hidden items-center gap-1.5 rounded-full bg-ink px-4 py-2 text-sm font-semibold text-bone transition-colors hover:bg-signal hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-signal sm:inline-flex'
                    >
                        Start a brief
                        <ArrowUpRight className='size-4' aria-hidden='true' />
                    </Link>

                    <button
                        type='button'
                        onClick={() => setOpen(value => !value)}
                        aria-expanded={open}
                        aria-controls='mobile-menu'
                        aria-label={open ? 'Close menu' : 'Open menu'}
                        className='inline-flex size-11 items-center justify-center rounded-full border border-ink/15 text-ink md:hidden focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-signal'
                    >
                        {open ? <X className='size-5' aria-hidden='true' /> : <Menu className='size-5' aria-hidden='true' />}
                    </button>
                </div>
            </div>

            <div
                id='mobile-menu'
                className={cn(
                    'border-t border-ink/10 bg-bone md:hidden',
                    open ? 'block' : 'hidden'
                )}
            >
                <nav aria-label='Mobile' className='mx-auto flex max-w-7xl flex-col px-5 py-4'>
                    {navLinks.map(link => (
                        <Link
                            key={link.href}
                            href={link.href}
                            onClick={() => setOpen(false)}
                            className='border-b border-ink/10 py-3.5 font-heading text-2xl text-ink'
                        >
                            {link.label}
                        </Link>
                    ))}

                    <Link
                        href='/#contact'
                        onClick={() => setOpen(false)}
                        className='mt-5 inline-flex items-center justify-center gap-1.5 rounded-full bg-signal px-5 py-3.5 text-sm font-semibold text-ink'
                    >
                        Start a brief
                        <ArrowUpRight className='size-4' aria-hidden='true' />
                    </Link>
                </nav>
            </div>
        </header>
    )
}

export default SiteHeader
